import { AZURE_CLIENT_APP_ID } from '@/config'
import { LogLevel, PublicClientApplication } from '@azure/msal-browser'

/**
 * The actual response is bigger, but this is the minimum needed for our logic to work
 */
export type FetchMeResponse = {
  userPrincipalName: string
}

export const fetchMe = async (token: string) => {
  const response = await fetch('https://graph.microsoft.com/v1.0/me', {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  })

  if (!response.ok && response.status == 401) {
    throw new Error('SharePoint access token is invalid or expired')
  }

  if (!response.ok) {
    throw new Error(`Failed to validate SharePoint token: ${response.statusText}`)
  }

  return response.json()
}

export const fetchSites = async (accessToken: string) => {
  // Get accessible SharePoint sites
  const r = await fetch('https://graph.microsoft.com/v1.0/sites?search=*', {
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
  })

  if (!r.ok) {
    throw new Error(`Failed to fetch SharePoint sites: ${r.status}`)
  }

  return r.json()
}

// MSAL Configuration for SharePoint authentication

let sharedApp: PublicClientApplication | null = null

export const resolveApp = async (): Promise<PublicClientApplication> => {
  if (sharedApp) {
    return sharedApp
  }

  sharedApp = new PublicClientApplication({
    auth: {
      clientId: AZURE_CLIENT_APP_ID,
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: window.location.origin,
    },
    cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
    system: {
      loggerOptions: { logLevel: LogLevel.Error, piiLoggingEnabled: false },
    },
  })

  await sharedApp.initialize()

  return sharedApp
}

/**
 * This follows microsofts instructions on how to get the iframe to activate correctly
 * It has to be done via DOM form submission,
 * as it's the only way to get past sandbox limitations of iframes
 */
export const activateIframe = (iframeName: string, filePickerUrl: string, accessToken: string) => {
  // Create a temporary form to POST to the iframe
  const tempForm = document.createElement('form')
  tempForm.style.display = 'none'
  tempForm.setAttribute('action', filePickerUrl)
  tempForm.setAttribute('method', 'POST')
  tempForm.setAttribute('target', iframeName)

  // Create hidden input for access token
  const tokenInput = document.createElement('input')
  tokenInput.setAttribute('type', 'hidden')
  tokenInput.setAttribute('name', 'access_token')
  tokenInput.setAttribute('value', accessToken)
  tempForm.appendChild(tokenInput)

  // Add to DOM
  document.body.appendChild(tempForm)

  // Submit form to iframe
  tempForm.submit()

  // Clean up temp form
  document.body.removeChild(tempForm)
}

/**
 * Not technically external api, but calls an "external" service, so
 * we need to occasionally stub it.
 */
export const getPickerChannelId = (): string => crypto.randomUUID()
